import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ExigePermissao, ForaDoColaborador } from './components/ExigePermissao';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { loginComRetorno } from './lib/retorno';

const pagina = <T extends Record<K, React.ComponentType>, K extends keyof T>(
  carregar: () => Promise<T>,
  nome: K,
) => lazy(() => carregar().then((modulo) => ({ default: modulo[nome] })));

const AuthCallbackPage = pagina(() => import('./pages/AuthCallbackPage'), 'AuthCallbackPage');
const ConfirmarPresencaPage = pagina(
  () => import('./pages/ConfirmarPresencaPage'),
  'ConfirmarPresencaPage',
);
const ColaboradoresPage = pagina(() => import('./pages/ColaboradoresPage'), 'ColaboradoresPage');
const ConveniaPage = pagina(() => import('./pages/ConveniaPage'), 'ConveniaPage');
const DashboardPage = pagina(() => import('./pages/DashboardPage'), 'DashboardPage');
const EscalaPage = pagina(() => import('./pages/EscalaPage'), 'EscalaPage');
const LoginPage = pagina(() => import('./pages/LoginPage'), 'LoginPage');
const MesasPage = pagina(() => import('./pages/MesasPage'), 'MesasPage');
const MinhaEquipePage = pagina(() => import('./pages/MinhaEquipePage'), 'MinhaEquipePage');
const PresencaPage = pagina(() => import('./pages/PresencaPage'), 'PresencaPage');
const SwilePage = pagina(() => import('./pages/SwilePage'), 'SwilePage');

const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      // Dados de ocupação mudam com frequência; refazer ao focar evita decidir com tela velha.
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

function Protegido({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  const local = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Verificando sessão…" />
      </div>
    );
  }
  // Volta para a página pedida depois do login (ex.: a tela do QR da mesa).
  return user ? children : <Navigate to={loginComRetorno(local.pathname + local.search)} replace />;
}

function Rotas(): JSX.Element {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <Spinner label="Carregando página…" />
        </div>
      }
    >
      <Routes>
        <Route path="/entrar" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* Tela aberta pelo QR da mesa: fora do layout, pensada para o celular. */}
        <Route
          path="/m/:token"
          element={
            <Protegido>
              <ConfirmarPresencaPage />
            </Protegido>
          }
        />
        <Route
          element={
            <Protegido>
              <Layout />
            </Protegido>
          }
        >
          <Route
            index
            element={
              <ForaDoColaborador>
                <DashboardPage />
              </ForaDoColaborador>
            }
          />
          <Route path="escala" element={<EscalaPage />} />
          {/* Todos os perfis reservam sala; o mapa dentro da página exige mapa:ver_disponibilidade. */}
          <Route
            path="mesas"
            element={
              <ExigePermissao permissao="sala:reservar">
                <MesasPage />
              </ExigePermissao>
            }
          />
          <Route
            path="swile"
            element={
              <ExigePermissao permissao="beneficio:calcular">
                <SwilePage />
              </ExigePermissao>
            }
          />
          <Route
            path="convenia"
            element={
              <ExigePermissao permissao="integracoes:sincronizar">
                <ConveniaPage />
              </ExigePermissao>
            }
          />
          <Route
            path="colaboradores"
            element={
              <ExigePermissao permissao="colaborador:ver">
                <ColaboradoresPage />
              </ExigePermissao>
            }
          />
          <Route
            path="minha-equipe"
            element={
              <ForaDoColaborador>
                <ExigePermissao permissao="escala:ver_propria">
                  <MinhaEquipePage />
                </ExigePermissao>
              </ForaDoColaborador>
            }
          />
          <Route path="presenca" element={<PresencaPage />} />
          <Route path="usuarios" element={<Navigate to="/colaboradores?aba=usuarios" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={cliente}>
      <AuthProvider>
        <Rotas />
      </AuthProvider>
    </QueryClientProvider>
  );
}
